// iter110n-3 · CEO 2026-09-28 · Procédures internes SHARITI.
// - Admin : liste des procédures, création, upload nouvelle version,
//   tableau de suivi (qui a signé quelle version).
// - Référent/Admin : liste "À signer" + accès aux récépissés signés,
//   avec signature électronique via SignaturePad (même certificat que
//   les contrats : horodatage, IP, SHA-256).

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    BookOpen, CheckCircle2, Clock, Download, FileText, FilePlus2, PenSquare,
    Upload, Users as UsersIcon, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import SignaturePad from "../components/signature/SignaturePad";
import { api } from "../lib/api";
import { openAuthedPdf } from "../lib/pdf";

export default function Procedures() {
    const [me, setMe] = useState(null);
    const [tab, setTab] = useState("mine");   // 'mine' (sign) | 'admin' (manage)
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setMe(data);
                // Default tab : admin voit "admin", référent voit "mine"
                if (data?.role === "admin") setTab("admin");
            } catch (e) {
                // ignore
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="p-8 text-center text-slate-500">Chargement…</div>
        );
    }

    const isAdmin = me?.role === "admin";

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600">
                    <BookOpen className="w-6 h-6" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Procédures internes</h1>
                    <p className="text-sm text-slate-500">
                        Documents applicables aux responsables d'équipe · signature électronique certifiée
                    </p>
                </div>
            </div>

            {/* Tabs — only relevant for admin (has both roles) */}
            {isAdmin && (
                <div className="border-b border-slate-200 flex gap-1">
                    <button
                        onClick={() => setTab("admin")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
                            tab === "admin" ? "border-orange-500 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                        data-testid="proc-tab-admin"
                    >
                        Espace admin
                    </button>
                    <button
                        onClick={() => setTab("mine")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
                            tab === "mine" ? "border-orange-500 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                        data-testid="proc-tab-mine"
                    >
                        Mes signatures
                    </button>
                </div>
            )}

            {tab === "admin" && isAdmin ? <AdminPanel /> : <MinePanel me={me} />}
        </div>
    );
}


// ─────────────────────────── Espace référent ───────────────────────────

function MinePanel({ me }) {
    const [pending, setPending] = useState([]);
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [signTarget, setSignTarget] = useState(null); // { procedure, active_version }

    const load = useCallback(async () => {
        try {
            const [{ data: p }, { data: r }] = await Promise.all([
                api.get("/procedures/mine/pending"),
                api.get("/procedures/mine/receipts"),
            ]);
            setPending(p.items || []);
            setReceipts(r.items || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6">
            {/* Pending — the big call to action */}
            <section>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" /> À signer ({pending.length})
                </div>
                {loading && (
                    <div className="text-sm text-slate-400 italic">Chargement…</div>
                )}
                {!loading && pending.length === 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-5 flex items-center gap-3" data-testid="proc-mine-empty-pending">
                        <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                        <div>
                            <div className="font-semibold text-green-900">Tout est signé</div>
                            <div className="text-sm text-green-700">
                                Vous êtes à jour sur toutes les procédures en vigueur.
                            </div>
                        </div>
                    </div>
                )}
                <div className="space-y-3">
                    {pending.map((it) => (
                        <div
                            key={it.active_version.id}
                            className="rounded-lg border border-orange-200 bg-orange-50/70 p-4 flex flex-wrap items-center gap-4"
                            data-testid={`proc-mine-pending-${it.procedure.id}`}
                        >
                            <FileText className="w-8 h-8 text-orange-500 shrink-0" />
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-semibold text-slate-900">{it.procedure.title}</div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                    Version <span className="font-mono font-bold">{it.active_version.version_label}</span>
                                    {" · "}
                                    {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(it.active_version.uploaded_at))}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => openAuthedPdf(`/procedures/versions/${it.active_version.id}/pdf`)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white hover:bg-slate-50"
                                data-testid={`proc-mine-view-${it.procedure.id}`}
                            >
                                <Download className="w-3.5 h-3.5" /> Consulter le PDF
                            </button>
                            <Button
                                onClick={() => setSignTarget(it)}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                data-testid={`proc-mine-sign-${it.procedure.id}`}
                            >
                                <PenSquare className="w-4 h-4 mr-1.5" />
                                Signer le récépissé
                            </Button>
                        </div>
                    ))}
                </div>
            </section>

            {/* Archived receipts */}
            <section>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
                    Mes récépissés signés ({receipts.length})
                </div>
                {receipts.length === 0 ? (
                    <div className="text-sm text-slate-400 italic">Aucun récépissé pour le moment.</div>
                ) : (
                    <div className="rounded-lg border border-slate-200 divide-y bg-white">
                        {receipts.map((r) => (
                            <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`proc-mine-receipt-${r.id}`}>
                                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                                <div className="flex-1 min-w-[220px]">
                                    <div className="text-sm font-semibold">{r.procedure_title}</div>
                                    <div className="text-xs text-slate-500">
                                        v{r.version_label} · signé le {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(r.signed_at))}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => openAuthedPdf(`/procedures/receipts/${r.id}/pdf`)}
                                    className="text-xs font-semibold text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
                                >
                                    <Download className="w-3.5 h-3.5" /> Récépissé PDF
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Sign modal */}
            {signTarget && (
                <SignReceiptModal
                    open={!!signTarget}
                    procedure={signTarget.procedure}
                    version={signTarget.active_version}
                    me={me}
                    onClose={() => setSignTarget(null)}
                    onSigned={() => { setSignTarget(null); load(); }}
                />
            )}
        </div>
    );
}


// ─────────────────────────── Sign Receipt Modal ───────────────────────────

function SignReceiptModal({ open, procedure, version, me, onClose, onSigned }) {
    const [sigDataUrl, setSigDataUrl] = useState(null);
    const [sigEmpty, setSigEmpty] = useState(true);
    const [saving, setSaving] = useState(false);
    const userFullName = me?.full_name || me?.name || me?.email || "";
    const receiptText = useMemo(() => (
        `Je soussigné(e) ${userFullName} reconnais avoir reçu et pris connaissance de la procédure ${procedure.title}, version ${version.version_label}, et m'engage à l'appliquer et à la faire appliquer par mon équipe.`
    ), [userFullName, procedure.title, version.version_label]);

    const submit = async () => {
        if (!sigDataUrl || sigEmpty) {
            toast.error("Dessinez votre signature");
            return;
        }
        setSaving(true);
        try {
            await api.post(
                `/procedures/${procedure.id}/versions/${version.id}/sign`,
                { signature_data_uri: sigDataUrl },
            );
            toast.success("Récépissé signé et archivé");
            onSigned?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de la signature");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="proc-sign-modal">
                <DialogHeader>
                    <DialogTitle>Récépissé — {procedure.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
                        <div>Procédure : <span className="font-semibold text-slate-900">{procedure.title}</span></div>
                        <div>Version : <span className="font-mono font-bold text-slate-900">{version.version_label}</span></div>
                        <div>Signataire : <span className="font-semibold text-slate-900">{userFullName}</span></div>
                    </div>
                    <div className="rounded-md border-2 border-dashed border-orange-300 bg-white p-4 text-sm italic text-slate-800 leading-relaxed">
                        « {receiptText} »
                    </div>
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                            Votre signature
                        </div>
                        <SignaturePad
                            onChange={(url, empty) => { setSigDataUrl(url); setSigEmpty(empty); }}
                            testIdPrefix="proc-sign"
                        />
                    </div>
                    <div className="text-[11px] text-slate-500 italic">
                        En signant, un récépissé PDF est généré avec un certificat électronique (horodatage, adresse IP, empreinte SHA-256) et archivé dans votre dossier.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
                    <Button onClick={submit} disabled={saving || sigEmpty} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="proc-sign-submit">
                        {saving ? "Signature…" : "Signer et archiver"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────── Espace admin ───────────────────────────

function AdminPanel() {
    const [procedures, setProcedures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creatingNew, setCreatingNew] = useState(false);
    const [uploadTarget, setUploadTarget] = useState(null); // procedure id
    const [statusTarget, setStatusTarget] = useState(null); // procedure id for tracking modal

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/procedures", { params: { include_inactive: true } });
            setProcedures(data.items || []);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                    {procedures.length} procédure{procedures.length > 1 ? "s" : ""}
                </div>
                <Button onClick={() => setCreatingNew(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="proc-admin-new">
                    <FilePlus2 className="w-4 h-4 mr-1.5" /> Nouvelle procédure
                </Button>
            </div>

            {loading ? (
                <div className="text-slate-400 text-sm italic">Chargement…</div>
            ) : procedures.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                    Aucune procédure. Créez la première pour commencer.
                </div>
            ) : (
                <div className="space-y-3">
                    {procedures.map((p) => (
                        <ProcedureCard
                            key={p.id}
                            procedure={p}
                            onUploadVersion={() => setUploadTarget(p.id)}
                            onOpenStatus={() => setStatusTarget(p.id)}
                            onRefresh={load}
                        />
                    ))}
                </div>
            )}

            {creatingNew && (
                <CreateProcedureModal
                    open={creatingNew}
                    onClose={() => setCreatingNew(false)}
                    onCreated={() => { setCreatingNew(false); load(); }}
                />
            )}
            {uploadTarget && (
                <UploadVersionModal
                    open={!!uploadTarget}
                    procedureId={uploadTarget}
                    onClose={() => setUploadTarget(null)}
                    onUploaded={() => { setUploadTarget(null); load(); }}
                />
            )}
            {statusTarget && (
                <TrackingModal
                    open={!!statusTarget}
                    procedureId={statusTarget}
                    onClose={() => setStatusTarget(null)}
                />
            )}
        </div>
    );
}


function ProcedureCard({ procedure, onUploadVersion, onOpenStatus, onRefresh }) {
    const [versions, setVersions] = useState([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!expanded) return;
        (async () => {
            try {
                const { data } = await api.get(`/procedures/${procedure.id}/versions`);
                setVersions(data.items || []);
            } catch (e) { /* ignore */ }
        })();
    }, [expanded, procedure.id]);

    return (
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`proc-admin-card-${procedure.id}`}>
            <div className="px-4 py-3 flex flex-wrap items-center gap-3">
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="text-slate-400 hover:text-slate-700"
                    aria-label={expanded ? "Réduire" : "Développer"}
                >
                    {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>
                <FileText className="w-8 h-8 text-orange-500" />
                <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold text-slate-900">{procedure.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        Créée le {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(procedure.created_at))}
                        {procedure.description ? ` · ${procedure.description}` : ""}
                    </div>
                </div>
                <Button
                    variant="outline" size="sm"
                    onClick={onOpenStatus}
                    data-testid={`proc-admin-status-${procedure.id}`}
                >
                    <UsersIcon className="w-3.5 h-3.5 mr-1.5" /> Suivi signatures
                </Button>
                <Button
                    variant="outline" size="sm"
                    onClick={onUploadVersion}
                    data-testid={`proc-admin-upload-${procedure.id}`}
                >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Nouvelle version
                </Button>
            </div>
            {expanded && (
                <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
                        Versions ({versions.length})
                    </div>
                    <div className="space-y-1.5">
                        {versions.map((v) => (
                            <div key={v.id} className="flex items-center gap-3 text-sm py-1">
                                <span className={`font-mono font-bold ${v.active ? "text-orange-600" : "text-slate-400"}`}>
                                    v{v.version_label}
                                </span>
                                {v.active && <span className="text-[10px] uppercase font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded">Active</span>}
                                <span className="text-xs text-slate-500 flex-1">
                                    {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(v.uploaded_at))}
                                    {" · "}
                                    {(v.pdf_size_bytes / 1024).toFixed(0)} Ko
                                </span>
                                <button
                                    type="button"
                                    onClick={() => openAuthedPdf(`/procedures/versions/${v.id}/pdf`)}
                                    className="text-xs font-semibold text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
                                >
                                    <Download className="w-3.5 h-3.5" /> PDF
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


function CreateProcedureModal({ open, onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [versionLabel, setVersionLabel] = useState("1.0");
    const [description, setDescription] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!title.trim() || !versionLabel.trim() || !file) {
            toast.error("Titre, version et PDF sont requis");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("title", title.trim());
            fd.append("version_label", versionLabel.trim());
            if (description.trim()) fd.append("description", description.trim());
            fd.append("pdf_file", file);
            await api.post("/procedures", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Procédure créée");
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="proc-admin-create-modal">
                <DialogHeader><DialogTitle>Nouvelle procédure</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Titre</label>
                        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex: Procédure de gestion des retours matériel" data-testid="proc-create-title" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Version</label>
                        <Input value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} placeholder="1.0" data-testid="proc-create-version" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Description (optionnel)</label>
                        <Input value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Fichier PDF</label>
                        <input
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-orange-50 file:text-orange-700 file:font-semibold hover:file:bg-orange-100"
                            data-testid="proc-create-file"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="proc-create-submit">
                        {saving ? "…" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function UploadVersionModal({ open, procedureId, onClose, onUploaded }) {
    const [versionLabel, setVersionLabel] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!versionLabel.trim() || !file) {
            toast.error("Version et PDF sont requis");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("version_label", versionLabel.trim());
            fd.append("pdf_file", file);
            await api.post(`/procedures/${procedureId}/versions`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Nouvelle version enregistrée · récépissé demandé à tous les responsables");
            onUploaded?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="proc-admin-upload-modal">
                <DialogHeader><DialogTitle>Nouvelle version</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                    <div className="text-xs text-slate-600 rounded-md bg-orange-50 border border-orange-200 px-3 py-2">
                        En déposant une nouvelle version, chaque responsable devra à nouveau signer un récépissé. Les versions précédentes et leurs récépissés restent archivés.
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nouvelle version</label>
                        <Input value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} placeholder="1.2, 2.0…" data-testid="proc-upload-version" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Fichier PDF</label>
                        <input
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-orange-50 file:text-orange-700 file:font-semibold hover:file:bg-orange-100"
                            data-testid="proc-upload-file"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="proc-upload-submit">
                        {saving ? "Envoi…" : "Enregistrer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────── Admin tracking table ───────────────────────────

function TrackingModal({ open, procedureId, onClose }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");   // 'signed' | 'pending' | ''
    const [q, setQ] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/procedures/${procedureId}/status`);
                setStatus(data);
            } catch (err) {
                toast.error("Erreur chargement suivi");
            } finally { setLoading(false); }
        })();
    }, [procedureId]);

    const signers = status?.signers || [];
    const stats = useMemo(() => {
        const signed = signers.filter((s) => !!s.receipt).length;
        return { signed, total: signers.length, pending: signers.length - signed };
    }, [signers]);
    const filtered = signers.filter((s) => {
        if (filter === "signed" && !s.receipt) return false;
        if (filter === "pending" && s.receipt) return false;
        if (q && !s.user.full_name.toLowerCase().includes(q.toLowerCase()) && !s.user.email?.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="proc-admin-tracking-modal">
                <DialogHeader>
                    <DialogTitle>
                        {loading ? "Chargement…" : `Suivi : ${status?.procedure?.title} — v${status?.active_version?.version_label || "—"}`}
                    </DialogTitle>
                </DialogHeader>
                {!loading && status && (
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2">
                                <div className="text-xs font-bold text-green-700 uppercase tracking-wider">Signés</div>
                                <div className="text-2xl font-black text-green-800">{stats.signed}</div>
                            </div>
                            <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
                                <div className="text-xs font-bold text-orange-700 uppercase tracking-wider">En attente</div>
                                <div className="text-2xl font-black text-orange-800">{stats.pending}</div>
                            </div>
                            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                                <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Total cibles</div>
                                <div className="text-2xl font-black text-slate-800">{stats.total}</div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <Input
                                value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="Rechercher un nom, un email…"
                                className="max-w-xs"
                                data-testid="proc-admin-tracking-search"
                            />
                            <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
                                <button onClick={() => setFilter("")} className={`px-3 py-1.5 font-semibold ${filter === "" ? "bg-slate-700 text-white" : "bg-white hover:bg-slate-50"}`}>Tous</button>
                                <button onClick={() => setFilter("signed")} className={`px-3 py-1.5 font-semibold ${filter === "signed" ? "bg-green-600 text-white" : "bg-white hover:bg-slate-50"}`}>Signés</button>
                                <button onClick={() => setFilter("pending")} className={`px-3 py-1.5 font-semibold ${filter === "pending" ? "bg-orange-500 text-white" : "bg-white hover:bg-slate-50"}`}>En attente</button>
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200 divide-y bg-white max-h-[52vh] overflow-y-auto">
                            {filtered.length === 0 && (
                                <div className="p-4 text-sm text-slate-400 italic text-center">Aucun résultat</div>
                            )}
                            {filtered.map((s) => (
                                <div key={s.user.id} className="px-3 py-2.5 flex flex-wrap items-center gap-3 text-sm" data-testid={`proc-admin-tracking-row-${s.user.id}`}>
                                    {s.receipt ? (
                                        <CheckCircle2 className="w-4.5 h-4.5 text-green-500 shrink-0" />
                                    ) : (
                                        <Clock className="w-4.5 h-4.5 text-orange-500 shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-[180px]">
                                        <div className="font-semibold text-slate-900">{s.user.full_name}</div>
                                        <div className="text-xs text-slate-500">{s.user.email} · {s.user.role}</div>
                                    </div>
                                    {s.receipt ? (
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-500">
                                                {new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(s.receipt.signed_at))}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => openAuthedPdf(`/procedures/receipts/${s.receipt.id}/pdf`)}
                                                className="text-xs font-semibold text-orange-600 hover:text-orange-800 inline-flex items-center gap-1"
                                            >
                                                <Download className="w-3.5 h-3.5" /> Récépissé
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-xs italic text-orange-600 font-semibold">Non signé</span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {status.other_receipts?.length > 0 && (
                            <div>
                                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2 mt-4">
                                    Récépissés d'anciennes versions ({status.other_receipts.length})
                                </div>
                                <div className="rounded-lg border border-slate-200 divide-y bg-white max-h-40 overflow-y-auto">
                                    {status.other_receipts.map((r) => (
                                        <div key={r.id} className="px-3 py-2 flex items-center gap-3 text-xs">
                                            <span className="font-mono font-bold text-slate-500">v{r.version_label}</span>
                                            <span className="flex-1">{r.user_full_name}</span>
                                            <span className="text-slate-500">
                                                {new Intl.DateTimeFormat("fr-FR", { dateStyle: "short" }).format(new Date(r.signed_at))}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => openAuthedPdf(`/procedures/receipts/${r.id}/pdf`)}
                                                className="text-orange-600 hover:text-orange-800 font-semibold"
                                            >PDF</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Fermer</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
